import React, { createContext, useContext, useEffect, ReactNode } from 'react'
import { useAppStore } from '@/app/store'
import { queryClient } from '@/app/providers/QueryProvider'
import { authService } from './service'
import { LoginCredentials, RegisterData, AuthState } from './types'

interface AuthContextValue extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>
  register: (data: RegisterData) => Promise<void>
  logout: () => Promise<void>
  hasRole: (role: string) => boolean
  hasAnyRole: (roles: string[]) => boolean
  isAdmin: boolean
  isExpert: boolean
  isClient: boolean
  isBanque: boolean
  isBureau: boolean
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { user, setUser, addNotification } = useAppStore()
  const [loading, setLoading] = React.useState(true)
  const [isAuthenticated, setIsAuthenticated] = React.useState(false)

  useEffect(() => {
    const token = localStorage.getItem('authToken')
    if (token) {
      verifyToken()
    } else {
      setLoading(false)
      setIsAuthenticated(false)
    }
  }, [])

  const verifyToken = async () => {
    try {
      const response = await authService.verifyToken()
      if (response.success) {
        setUser(response.data.user)
        setIsAuthenticated(true)
      } else {
        throw new Error('Token verification failed')
      }
    } catch (error) {
      console.error('Token verification failed:', error)
      localStorage.removeItem('authToken')
      setUser(null)
      setIsAuthenticated(false)
    } finally {
      setLoading(false)
    }
  }

  const login = async (credentials: LoginCredentials) => {
    try {
      const response = await authService.login(credentials)
      if (response.success) {
        setUser(response.data.user)
        setIsAuthenticated(true)
      } else {
        throw new Error(response.message || 'Login failed')
      }
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Login failed',
        message: error.message || 'An error occurred during login'
      })
      throw error
    }
  }

  const register = async (data: RegisterData) => {
    try {
      const response = await authService.register(data)
      if (response.success) {
        setUser(response.data.user)
        setIsAuthenticated(true)
        addNotification({
          type: 'success',
          title: 'Registration successful',
          message: 'Welcome to ChatelainXpert!'
        })
      } else {
        throw new Error(response.message || 'Registration failed')
      }
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Registration failed',
        message: error.message || 'An error occurred during registration'
      })
      throw error
    }
  }

  const logout = async () => {
    await authService.logout()
    // Clear all React Query cache to prevent stale data for next user
    queryClient.clear()
    setUser(null)
    setIsAuthenticated(false)
  }

  const hasRole = (role: string): boolean => {
    if (!user) return false
    if (user.roles && Array.isArray(user.roles)) {
      return user.roles.includes(role)
    }
    return user.role === role
  }

  const hasAnyRole = (roles: string[]): boolean => {
    if (!user) return false
    return roles.some(role => hasRole(role))
  }

  const isAdmin = hasRole('admin')
  const isExpert = hasRole('expert')
  const isClient = hasRole('client')
  const isBanque = hasRole('banque')
  const isBureau = hasRole('bureau')

  const value: AuthContextValue = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    hasRole,
    hasAnyRole,
    isAdmin,
    isExpert,
    isClient,
    isBanque,
    isBureau,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}